/**
 * Shared chrome for the public, non-auth marketing pages (About, Contact, the
 * legal pages required for Razorpay merchant approval).
 *
 * Renders MarketingNav + a marketing-style footer around children, so each
 * page file only ships its own copy.
 */

import type { ReactNode } from 'react'
import { Logo } from './Logo.js'
import { MarketingNav } from './MarketingNav.js'

interface MarketingPageProps {
  eyebrow?: string
  title: string
  /** Updated date displayed under the title on legal pages. */
  effectiveDate?: string
  children: ReactNode
}

export function MarketingPage({
  eyebrow,
  title,
  effectiveDate,
  children,
}: MarketingPageProps) {
  return (
    <div className="min-h-screen flex flex-col bg-surface">
      <MarketingNav variant="auth" />
      <main className="flex-1">
        <section className="px-6 pt-16 pb-10 bg-gradient-to-b from-surface-muted to-surface">
          <div className="max-w-3xl mx-auto">
            {eyebrow ? (
              <p className="text-xs font-medium uppercase tracking-wider text-[var(--color-primary)]">
                {eyebrow}
              </p>
            ) : null}
            <h1 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight text-fg leading-tight">
              {title}
            </h1>
            {effectiveDate ? (
              <p className="mt-3 text-sm text-fg-subtle">
                Effective {effectiveDate}
              </p>
            ) : null}
          </div>
        </section>
        <section className="px-6 pb-24">
          <div className="max-w-3xl mx-auto prose-page">{children}</div>
        </section>
      </main>
      <MarketingFooter />
    </div>
  )
}

/**
 * Shared footer for marketing + legal pages.
 * Kept in sync with the footer on the landing page so links to legal pages
 * always resolve from anywhere on the marketing surface.
 */
export function MarketingFooter() {
  return (
    <footer className="border-t border-border bg-surface">
      <div className="max-w-6xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-sm">
        <div className="col-span-2 md:col-span-1">
          <Logo />
          <p className="mt-3 text-xs text-fg-subtle leading-relaxed">
            All-in-one software for coaching academies in India: fees,
            attendance, tests, and parent communication.
          </p>
        </div>
        <FooterColumn
          heading="Product"
          links={[
            { label: 'Features', href: '/#features' },
            { label: 'Pricing', href: '/#pricing' },
            { label: 'Sign in', href: '/login' },
          ]}
        />
        <FooterColumn
          heading="Company"
          links={[
            { label: 'About us', href: '/about' },
            { label: 'Contact us', href: '/contact' },
          ]}
        />
        <FooterColumn
          heading="Legal"
          links={[
            { label: 'Terms of service', href: '/terms' },
            { label: 'Privacy policy', href: '/privacy' },
            { label: 'Refund & cancellation', href: '/refund' },
          ]}
        />
      </div>
      <div className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-fg-subtle">
          <span>© {new Date().getFullYear()} Brightstars. All rights reserved.</span>
          <a
            href="mailto:hello@brightstars.in"
            className="hover:text-fg transition-colors"
          >
            hello@brightstars.in
          </a>
        </div>
      </div>
    </footer>
  )
}

function FooterColumn({
  heading,
  links,
}: {
  heading: string
  links: { label: string; href: string }[]
}) {
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-fg-subtle">
        {heading}
      </h3>
      <ul className="mt-3 space-y-2">
        {links.map((link) => (
          <li key={link.href}>
            <a
              href={link.href}
              className="text-fg-muted hover:text-fg transition-colors"
            >
              {link.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}
